import { useEffect, useRef, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { homePathFor } from '../constants/navigation'
import { useSelectedPropertyType } from '../store/propertyStore'
import { useUiStore } from '../store/uiStore'
import { PropertySelect } from './layout/PropertySelect'

const GLIDE_MS = 620
// Eases in slightly before a long, flat decay, so it settles rather than stops.
const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)'

// The two ends of the flight. Swapping between them mid-glide is what morphs
// the tall, shadowed landing field into the flat one the top bar wears.
const FIELD_BASE =
  // Same duration and curve as the flight, or the field finishes changing shape
  // before it arrives.
  'w-full appearance-none truncate rounded-lg bg-white px-4 pr-10 text-[14px] text-gray-700 outline-none transition-all duration-[620ms] ease-[cubic-bezier(0.32,0.72,0,1)] disabled:cursor-not-allowed disabled:opacity-50'
const FIELD_AT_REST = `${FIELD_BASE} h-11 border border-gray-300 shadow-sm focus:border-brand-green`
const FIELD_IN_BAR = `${FIELD_BASE} h-9 border border-gray-200 shadow-none`

// Where the selector is flying to. The top bar renders the same selector once
// per breakpoint and only one of them is laid out, so ask the document which
// copy is actually on screen rather than guessing at a media query. Returns
// null when neither is measurable — no layout in jsdom, and no bar at all if
// this ever renders outside AppLayout.
function topBarSelectRect(): DOMRect | null {
  const onScreen = Array.from(
    document.querySelectorAll<HTMLElement>('header [aria-label="Selected property"]'),
  ).find((el) => el.offsetParent !== null && el.getBoundingClientRect().width > 0)
  return onScreen?.getBoundingClientRect() ?? null
}

// 'idle'   — nothing chosen, the picker sits in the middle of the page
// 'lift'   — chosen: pinned to the viewport where it already was, so the flight
//            escapes AppLayout, every ancestor of which clips overflow
// 'fly'    — transform applied, gliding up to the top bar
// 'landed' — animation over, hand off to the router
type Phase = 'idle' | 'lift' | 'fly' | 'landed'

interface Flight {
  dx: number
  dy: number
  from: DOMRect
  to: DOMRect
}

/**
 * The landing a signed-in user gets before they have picked a property.
 *
 * It shows the top bar's own selector in the middle of the page — asking
 * someone to hunt for a dropdown they have never seen is the worst moment of
 * the app — then glides that selector up into the top bar once they choose, so
 * they learn where it lives for next time.
 */
export function PropertyLanding(): JSX.Element {
  const propertyType = useSelectedPropertyType()

  // A property already chosen at mount means a reload at "/" with the previous
  // pick still in sessionStorage. There is no flight from a picker that was
  // never on screen, so go straight through. State, not a ref: the first render
  // reads it to decide whether to show anything at all.
  const [startedEmpty] = useState(() => propertyType === null)

  const [phase, setPhase] = useState<Phase>('idle')
  const [flight, setFlight] = useState<Flight | null>(null)
  const fieldRef = useRef<HTMLDivElement>(null)
  const setInFlight = useUiStore((s) => s.setPropertySelectorInFlight)

  // While this page owns the selector the top bar hides its own copy, so the
  // user is never choosing between two of them.
  useEffect(() => {
    if (!startedEmpty || phase === 'landed') return
    setInFlight(true)
    return () => setInFlight(false)
  }, [startedEmpty, phase, setInFlight])

  useEffect(() => {
    if (!propertyType || phase !== 'idle') return

    const from = fieldRef.current?.getBoundingClientRect()
    const to = topBarSelectRect()
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches

    // Nothing to fly to, or the user asked not to be flown anywhere.
    if (!from || !to || reduceMotion) {
      setPhase('landed')
      return
    }

    setFlight({ dx: to.left - from.left, dy: to.top - from.top, from, to })
    setPhase('lift')
  }, [propertyType, phase])

  // Pinning and moving have to be two separate paints or the browser has no
  // start position to interpolate from and the element simply appears there.
  useEffect(() => {
    if (phase !== 'lift') return
    const raf = requestAnimationFrame(() => setPhase('fly'))
    return () => cancelAnimationFrame(raf)
  }, [phase])

  // ponytail: a timer rather than transitionend — two properties transition, so
  // the event fires twice and needs filtering, and a missed event would strand
  // the user on this page forever. The timer cannot not fire.
  useEffect(() => {
    if (phase !== 'fly') return
    const t = setTimeout(() => setPhase('landed'), GLIDE_MS)
    return () => clearTimeout(t)
  }, [phase])

  if (propertyType && (!startedEmpty || phase === 'landed')) {
    return <Navigate to={homePathFor(propertyType)} replace />
  }

  const flying = phase === 'lift' || phase === 'fly'

  // Pinned to the viewport for the flight, so no clipping ancestor can crop it.
  const fieldStyle: React.CSSProperties = flying && flight
    ? {
        position: 'fixed',
        left: flight.from.left,
        top: flight.from.top,
        width: phase === 'fly' ? flight.to.width : flight.from.width,
        transform: phase === 'fly' ? `translate(${flight.dx}px, ${flight.dy}px)` : undefined,
        transition: `transform ${GLIDE_MS}ms ${EASE}, width ${GLIDE_MS}ms ${EASE}`,
        zIndex: 50,
      }
    : {}

  return (
    <div className="flex min-h-full flex-col items-center justify-center px-4 py-12 text-center">
      <div
        className={`transition-opacity duration-200 ${flying ? 'opacity-0' : 'opacity-100'}`}
      >
        <h1 className="text-[20px] font-bold text-gray-900">Which property are you working on?</h1>
        <p className="mt-1 text-[14px] text-gray-500">
          Pick one to see its pages. You can switch any time from the top bar.
        </p>
      </div>

      {/* Margin sits out here: a fixed element still obeys its own margin-top,
          which landed the flier 24px below the bar. */}
      <div className="mt-6 h-11 w-full max-w-[320px]">
        <div ref={fieldRef} style={fieldStyle} className="relative w-full">
          <PropertySelect
            className={phase === 'fly' ? FIELD_IN_BAR : FIELD_AT_REST}
            chevronCls="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
        </div>
      </div>
    </div>
  )
}
